import axios from 'axios';

/**
 * Deal Service
 * Handles all API calls related to deals, pipelines, and activities
 */
export const dealService = {
  // ============================================
  // PIPELINES
  // ============================================

  /**
   * Get all pipelines for the organization
   * @returns {Promise<Object>} { pipelines: Pipeline[] }
   */
  async getPipelines() {
    try {
      const response = await axios.get('/pipelines');
      return response.data;
    } catch (error) {
      console.error('Error fetching pipelines:', error);
      throw error;
    }
  },

  /**
   * Get the default pipeline with stages
   * @returns {Promise<Pipeline>}
   */
  async getDefaultPipeline() {
    try {
      const response = await axios.get('/pipelines/default');
      return response.data;
    } catch (error) {
      console.error('Error fetching default pipeline:', error);
      throw error;
    }
  },

  /**
   * Create a new pipeline
   * @param {Object} pipelineData - { name, description?, is_default? }
   * @returns {Promise<Pipeline>}
   */
  async createPipeline(pipelineData) {
    try {
      const response = await axios.post('/pipelines', pipelineData);
      return response.data;
    } catch (error) {
      console.error('Error creating pipeline:', error);
      throw error;
    }
  },

  /**
   * Update a pipeline
   * @param {string} pipelineId
   * @param {Object} pipelineData
   * @returns {Promise<Pipeline>}
   */
  async updatePipeline(pipelineId, pipelineData) {
    try {
      const response = await axios.patch(`/pipelines/${pipelineId}`, pipelineData);
      return response.data;
    } catch (error) {
      console.error('Error updating pipeline:', error);
      throw error;
    }
  },

  /**
   * Delete a pipeline
   * @param {string} pipelineId
   * @returns {Promise<void>}
   */
  async deletePipeline(pipelineId) {
    try {
      await axios.delete(`/pipelines/${pipelineId}`);
    } catch (error) {
      console.error('Error deleting pipeline:', error);
      throw error;
    }
  },

  /**
   * Set a pipeline as default
   * @param {string} pipelineId
   * @returns {Promise<Pipeline>}
   */
  async setDefaultPipeline(pipelineId) {
    try {
      const response = await axios.post(`/pipelines/${pipelineId}/set-default`);
      return response.data;
    } catch (error) {
      console.error('Error setting default pipeline:', error);
      throw error;
    }
  },

  // ============================================
  // PIPELINE STAGES
  // ============================================

  /**
   * Get stages for a pipeline
   * @param {string} pipelineId
   * @returns {Promise<Object>} { stages: PipelineStage[] }
   */
  async getPipelineStages(pipelineId) {
    try {
      const response = await axios.get(`/pipelines/${pipelineId}/stages`);
      return response.data;
    } catch (error) {
      console.error('Error fetching pipeline stages:', error);
      throw error;
    }
  },

  /**
   * Create a new stage
   * @param {string} pipelineId
   * @param {Object} stageData - { name, color, probability, is_won, is_lost, auto_trigger? }
   * @returns {Promise<PipelineStage>}
   */
  async createStage(pipelineId, stageData) {
    try {
      const response = await axios.post(`/pipelines/${pipelineId}/stages`, stageData);
      return response.data;
    } catch (error) {
      console.error('Error creating stage:', error);
      throw error;
    }
  },

  /**
   * Update a stage
   * @param {string} pipelineId
   * @param {string} stageId
   * @param {Object} stageData
   * @returns {Promise<PipelineStage>}
   */
  async updateStage(pipelineId, stageId, stageData) {
    try {
      const response = await axios.patch(
        `/pipelines/${pipelineId}/stages/${stageId}`,
        stageData
      );
      return response.data;
    } catch (error) {
      console.error('Error updating stage:', error);
      throw error;
    }
  },

  /**
   * Delete a stage
   * @param {string} pipelineId
   * @param {string} stageId
   * @returns {Promise<void>}
   */
  async deleteStage(pipelineId, stageId) {
    try {
      await axios.delete(`/pipelines/${pipelineId}/stages/${stageId}`);
    } catch (error) {
      console.error('Error deleting stage:', error);
      throw error;
    }
  },

  /**
   * Reorder stages
   * @param {string} pipelineId
   * @param {string[]} stageIds - Ordered array of stage IDs
   * @returns {Promise<Object>}
   */
  async reorderStages(pipelineId, stageIds) {
    try {
      const response = await axios.patch(`/pipelines/${pipelineId}/stages/reorder`, {
        stage_ids: stageIds,
      });
      return response.data;
    } catch (error) {
      console.error('Error reordering stages:', error);
      throw error;
    }
  },

  // ============================================
  // DEALS
  // ============================================

  /**
   * Get deals with filters
   * @param {Object} params - { pipeline_id?, stage_id?, contact_id?, owner_id?, search?, status?, page?, limit?, lead_id? }
   * @returns {Promise<Object>} { deals: Deal[], pagination: Pagination }
   */
  async getDeals(params = {}) {
    try {
      const response = await axios.get('/deals', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching deals:', error);
      throw error;
    }
  },

  /**
   * Get deals by lead ID with their activities
   * @param {string} leadId - The lead ID
   * @returns {Promise<Object>} { deals: Deal[] }
   */
  async getDealsByLead(leadId) {
    try {
      const response = await axios.get('/deals', {
        params: { lead_id: leadId, include_activities: true },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching deals by lead:', error);
      throw error;
    }
  },

  /**
   * Get deals in kanban format
   * @param {string} pipelineId
   * @param {Object} params - { owner_id?, search?, tag_ids? } where tag_ids is a
   *   comma-separated list of tag uuids (deal matches if it has ANY of them)
   * @returns {Promise<Object>} { pipeline, stages: StageWithDeals[], totals }
   */
  async getKanban(pipelineId, params = {}) {
    try {
      const response = await axios.get('/deals/kanban', {
        params: { pipeline_id: pipelineId, ...params },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching kanban data:', error);
      throw error;
    }
  },

  /**
   * Export deals (with linked lead + quote data) as an Excel or CSV file
   * @param {Object} params - { pipeline_id?, stage_ids?, stage_id?, owner_id?, tag_ids?, status?, search?, format?, granularity? }
   *   format is `xlsx` (two sheets) or `csv`; granularity is `deals` or `items` (csv only)
   * @returns {Promise<{ blob: Blob, filename: string }>}
   */
  async exportDeals(params = {}) {
    try {
      const response = await axios.get('/deals/export', {
        params,
        responseType: 'blob',
      });

      // Try to read the filename from the Content-Disposition header,
      // falling back to a sensible default with today's date.
      const disposition = response.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const filename = match
        ? decodeURIComponent(match[1])
        : `deals-export-${new Date().toISOString().slice(0, 10)}.xlsx`;

      return { blob: response.data, filename };
    } catch (error) {
      console.error('Error exporting deals:', error);
      throw error;
    }
  },

  /**
   * Get deal statistics
   * @param {Object} params - { period?: '7d' | '30d' | '90d' | '365d' }
   * @returns {Promise<Object>} Stats object
   */
  async getStats(params = {}) {
    try {
      const response = await axios.get('/deals/stats', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching deal stats:', error);
      throw error;
    }
  },

  /**
   * Get a single deal by ID
   * @param {string} dealId
   * @returns {Promise<Deal>}
   */
  async getDealById(dealId) {
    try {
      const response = await axios.get(`/deals/${dealId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching deal:', error);
      throw error;
    }
  },

  /**
   * Create a new deal
   * @param {Object} dealData
   * @returns {Promise<Deal>}
   */
  async createDeal(dealData) {
    try {
      const response = await axios.post('/deals', dealData);
      return response.data;
    } catch (error) {
      console.error('Error creating deal:', error);
      throw error;
    }
  },

  /**
   * Update a deal
   * @param {string} dealId
   * @param {Object} dealData
   * @returns {Promise<Deal>}
   */
  async updateDeal(dealId, dealData) {
    try {
      const response = await axios.patch(`/deals/${dealId}`, dealData);
      return response.data;
    } catch (error) {
      console.error('Error updating deal:', error);
      throw error;
    }
  },

  /**
   * Delete a deal
   * @param {string} dealId
   * @returns {Promise<void>}
   */
  async deleteDeal(dealId) {
    try {
      await axios.delete(`/deals/${dealId}`);
    } catch (error) {
      console.error('Error deleting deal:', error);
      throw error;
    }
  },

  /**
   * Move deal to a different stage
   * @param {string} dealId
   * @param {string} stageId
   * @returns {Promise<Deal>}
   */
  async moveToStage(dealId, stageId) {
    try {
      const response = await axios.patch(`/deals/${dealId}/stage`, { stage_id: stageId });
      return response.data;
    } catch (error) {
      console.error('Error moving deal to stage:', error);
      throw error;
    }
  },

  /**
   * Mark deal as won
   * @param {string} dealId
   * @returns {Promise<Deal>}
   */
  async markAsWon(dealId) {
    try {
      const response = await axios.post(`/deals/${dealId}/won`);
      return response.data;
    } catch (error) {
      console.error('Error marking deal as won:', error);
      throw error;
    }
  },

  /**
   * Mark deal as lost
   * @param {string} dealId
   * @param {string} reason
   * @returns {Promise<Deal>}
   */
  async markAsLost(dealId, reason = null) {
    try {
      const response = await axios.post(`/deals/${dealId}/lost`, { reason });
      return response.data;
    } catch (error) {
      console.error('Error marking deal as lost:', error);
      throw error;
    }
  },

  /**
   * Reopen a closed deal
   * @param {string} dealId
   * @returns {Promise<Deal>}
   */
  async reopenDeal(dealId) {
    try {
      const response = await axios.post(`/deals/${dealId}/reopen`);
      return response.data;
    } catch (error) {
      console.error('Error reopening deal:', error);
      throw error;
    }
  },

  /**
   * Merge deals
   * @param {string} targetDealId
   * @param {string[]} sourceDealIds
   * @returns {Promise<Deal>}
   */
  async mergeDeals(targetDealId, sourceDealIds) {
    try {
      const response = await axios.post(`/deals/${targetDealId}/merge`, {
        source_deal_ids: sourceDealIds,
      });
      return response.data;
    } catch (error) {
      console.error('Error merging deals:', error);
      throw error;
    }
  },

  // ============================================
  // BULK OPERATIONS
  // ============================================

  /**
   * Bulk move deals to stage
   * @param {string[]} dealIds
   * @param {string} stageId
   * @returns {Promise<Object>}
   */
  async bulkMoveToStage(dealIds, stageId) {
    try {
      const response = await axios.post('/deals/bulk/move-stage', {
        deal_ids: dealIds,
        stage_id: stageId,
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk moving deals:', error);
      throw error;
    }
  },

  /**
   * Bulk assign deals to owner
   * @param {string[]} dealIds
   * @param {string} ownerId
   * @returns {Promise<Object>}
   */
  async bulkAssign(dealIds, ownerId) {
    try {
      const response = await axios.post('/deals/bulk/assign', {
        deal_ids: dealIds,
        owner_id: ownerId,
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk assigning deals:', error);
      throw error;
    }
  },

  /**
   * Bulk archive deals
   * @param {string[]} dealIds
   * @returns {Promise<Object>}
   */
  async bulkArchive(dealIds) {
    try {
      const response = await axios.post('/deals/bulk/archive', {
        deal_ids: dealIds,
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk archiving deals:', error);
      throw error;
    }
  },

  // ============================================
  // DEAL FORM LINKS
  // ============================================

  /**
   * Get form links for a deal
   * @param {string} dealId
   * @returns {Promise<Object>} { links: DealFormLink[] }
   */
  async getFormLinks(dealId) {
    try {
      const response = await axios.get(`/deals/${dealId}/form-links`);
      return response.data;
    } catch (error) {
      console.error('Error fetching deal form links:', error);
      throw error;
    }
  },

  /**
   * Create a form link for a deal
   * @param {string} dealId
   * @param {Object} linkData - { form_id, prefill_contact?, expires_at? }
   * @returns {Promise<DealFormLink>}
   */
  async createFormLink(dealId, linkData) {
    try {
      const response = await axios.post(`/deals/${dealId}/form-links`, linkData);
      return response.data;
    } catch (error) {
      console.error('Error creating form link:', error);
      throw error;
    }
  },

  /**
   * Delete a form link
   * @param {string} dealId
   * @param {string} linkId
   * @returns {Promise<void>}
   */
  async deleteFormLink(dealId, linkId) {
    try {
      await axios.delete(`/deals/${dealId}/form-links/${linkId}`);
    } catch (error) {
      console.error('Error deleting form link:', error);
      throw error;
    }
  },

  // ============================================
  // DEAL NESTED RESOURCES
  // ============================================

  /**
   * Get running customer journeys for a pipeline, grouped client-side per phase
   * @param {Object} params - { pipeline_id, owner_id?, search?, tag_ids? }
   * @returns {Promise<Object>} { phases: string[], deals: Deal[] (each with `journey`) }
   */
  async getJourneyBoard(params) {
    try {
      const response = await axios.get('/deals/journey-board', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching journey board:', error);
      throw error;
    }
  },

  /**
   * Get journey next steps the organisation has to act on, across all pipelines
   * @param {Object} params - { owner_ids?: comma-separated, include_unassigned?: boolean }
   * @returns {Promise<Object>} { actions: Deal[] (each with `journey`) }
   */
  async getJourneyActions(params) {
    try {
      const response = await axios.get('/deals/journey-actions', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching journey actions:', error);
      throw error;
    }
  },

  /**
   * Get the customer journey for a deal (phase, who has the ball, next step)
   * @param {string} dealId
   * @returns {Promise<Object>} { status, phase, phases, ball, next_step, waiting_since, progress }
   */
  async getDealJourney(dealId) {
    try {
      const response = await axios.get(`/deals/${dealId}/journey`);
      return response.data;
    } catch (error) {
      console.error('Error fetching deal journey:', error);
      throw error;
    }
  },

  /**
   * Get proposals for a deal
   * @param {string} dealId
   * @returns {Promise<Object>} { proposals: Proposal[] }
   */
  async getDealProposals(dealId) {
    try {
      const response = await axios.get(`/deals/${dealId}/proposals`);
      return response.data;
    } catch (error) {
      console.error('Error fetching deal proposals:', error);
      throw error;
    }
  },

  /**
   * Get submissions for a deal
   * @param {string} dealId
   * @returns {Promise<Object>} { submissions: Submission[] }
   */
  async getDealSubmissions(dealId) {
    try {
      const response = await axios.get(`/deals/${dealId}/submissions`);
      return response.data;
    } catch (error) {
      console.error('Error fetching deal submissions:', error);
      throw error;
    }
  },

  /**
   * Get activities for a deal
   * @param {string} dealId
   * @returns {Promise<Object>} { activities: Activity[] }
   */
  async getDealActivities(dealId) {
    try {
      const response = await axios.get(`/deals/${dealId}/activities`);
      return response.data;
    } catch (error) {
      console.error('Error fetching deal activities:', error);
      throw error;
    }
  },

  /**
   * Create an activity for a deal
   * @param {string} dealId
   * @param {Object} activityData - { type, subject, description?, outcome? }
   * @returns {Promise<Activity>} API returns { activity: Activity }
   */
  async createDealActivity(dealId, activityData) {
    try {
      const response = await axios.post(`/deals/${dealId}/activities`, activityData);
      const data = response.data;
      return data?.activity ?? data;
    } catch (error) {
      console.error('Error creating deal activity:', error);
      throw error;
    }
  },

  // ============================================
  // ACTIVITIES (STANDALONE)
  // ============================================

  /**
   * Get activities with filters
   * @param {Object} params - { deal_id?, contact_id?, type?, assigned_to?, page?, limit? }
   * @returns {Promise<Object>} { activities: Activity[], pagination: Pagination }
   */
  async getActivities(params = {}) {
    try {
      const response = await axios.get('/activities', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching activities:', error);
      throw error;
    }
  },

  /**
   * Create an activity
   * @param {Object} activityData
   * @returns {Promise<Activity>}
   */
  async createActivity(activityData) {
    try {
      const response = await axios.post('/activities', activityData);
      return response.data;
    } catch (error) {
      console.error('Error creating activity:', error);
      throw error;
    }
  },

  /**
   * Update an activity
   * @param {string} activityId
   * @param {Object} activityData
   * @returns {Promise<Activity>}
   */
  async updateActivity(activityId, activityData) {
    try {
      const response = await axios.patch(`/activities/${activityId}`, activityData);
      return response.data;
    } catch (error) {
      console.error('Error updating activity:', error);
      throw error;
    }
  },

  /**
   * Delete an activity
   * @param {string} activityId
   * @returns {Promise<void>}
   */
  async deleteActivity(activityId) {
    try {
      await axios.delete(`/activities/${activityId}`);
    } catch (error) {
      console.error('Error deleting activity:', error);
      throw error;
    }
  },

  /**
   * Toggle favorite status for a deal
   * @param {string} dealId
   * @param {boolean} isFavorite
   * @returns {Promise<Deal>}
   */
  async toggleFavorite(dealId, isFavorite) {
    try {
      const response = await axios.patch(`/deals/${dealId}`, { is_favorite: isFavorite });
      return response.data;
    } catch (error) {
      console.error('Error toggling deal favorite:', error);
      throw error;
    }
  },

  // ============================================
  // DEAL TASKS
  // ============================================

  /**
   * Get tasks for a specific deal
   * @param {string} dealId
   * @returns {Promise<Object>} { tasks: Task[] }
   */
  async getDealTasks(dealId) {
    try {
      const response = await axios.get(`/deals/${dealId}/tasks`);
      return response.data;
    } catch (error) {
      console.error('Error fetching deal tasks:', error);
      throw error;
    }
  },

  /**
   * Create a task for a deal
   * @param {string} dealId
   * @param {Object} taskData
   * @returns {Promise<Task>}
   */
  async createDealTask(dealId, taskData) {
    try {
      const response = await axios.post(`/deals/${dealId}/tasks`, taskData);
      return response.data;
    } catch (error) {
      console.error('Error creating deal task:', error);
      throw error;
    }
  },

  /**
   * Update a task
   * @param {string} taskId
   * @param {Object} taskData
   * @returns {Promise<Task>}
   */
  async updateTask(taskId, taskData) {
    try {
      const response = await axios.patch(`/tasks/${taskId}`, taskData);
      return response.data;
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  },

  /**
   * Delete a task
   * @param {string} taskId
   * @returns {Promise<void>}
   */
  async deleteTask(taskId) {
    try {
      await axios.delete(`/tasks/${taskId}`);
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  },

  /**
   * Mark a task as completed
   * @param {string} taskId
   * @returns {Promise<Task>}
   */
  async completeTask(taskId) {
    try {
      const response = await axios.post(`/tasks/${taskId}/complete`);
      return response.data;
    } catch (error) {
      console.error('Error completing task:', error);
      throw error;
    }
  },

  /**
   * Reopen a completed task
   * @param {string} taskId
   * @returns {Promise<Task>}
   */
  async reopenTask(taskId) {
    try {
      const response = await axios.post(`/tasks/${taskId}/reopen`);
      return response.data;
    } catch (error) {
      console.error('Error reopening task:', error);
      throw error;
    }
  },
};

export default dealService;
